import { useEffect, useState } from 'react';
import { Fuel, Link2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchCiternes, deleteCiterne, fetchCiterneConsommations } from '@/store/thunks/citerneThunks';
import { fetchAffectations, deleteAffectation } from '@/store/thunks/affectationThunks';
import type { Citerne, Jauge } from '@/types';
import type { CiterneAffectation } from '@/types';
import { checkPermission } from '@/utils/checkPermission';
import { filterListByQuery } from '@/utils/filterList';
import Page from '@/components/Page';
import { AddButton, FilterInput, Modal, DataTable, ConfirmModal, Tabs, TabPanel, Pagination, TableActions } from '@/components/ui';
import CiterneForm from '@/components/citerne/CiterneForm';
import CiterneAffectationForm from '@/components/citerne/CiterneAffectationForm';
import CiterneCard from '@/components/citerne/CiterneCard';
import JaugerForm from '@/components/citerne/JaugerForm';

export default function CiternePage() {
  const dispatch = useAppDispatch();
  const { citernes, consommations } = useAppSelector((s: RootState) => s.citerne);
  const { affectations, pageNum, totalPages } = useAppSelector((s: RootState) => s.affectation);
  const { isAdmin, authorities } = useAppSelector((s: RootState) => s.auth);
  const [tab, setTab] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [affectModalOpen, setAffectModalOpen] = useState(false);
  const [jaugeModalOpen, setJaugeModalOpen] = useState(false);
  const [citerneForJauge, setCiterneForJauge] = useState<Citerne | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [affectConfirmOpen, setAffectConfirmOpen] = useState(false);
  const [editing, setEditing] = useState<Citerne | null>(null);
  const [editingAffectation, setEditingAffectation] = useState<CiterneAffectation | null>(null);
  const [toDelete, setToDelete] = useState<Citerne | null>(null);
  const [toDeleteAffectation, setToDeleteAffectation] = useState<CiterneAffectation | null>(null);
  const [filterQuery, setFilterQuery] = useState('');
  const [affectFilterQuery, setAffectFilterQuery] = useState('');

  useEffect(() => {
    if (tab === 0) {
      dispatch(fetchCiternes(undefined));
      dispatch(fetchCiterneConsommations());
    }
  }, [tab, dispatch]);

  useEffect(() => {
    if (tab === 1) dispatch(fetchAffectations(0));
  }, [tab, dispatch]);

  const canCreate = isAdmin || checkPermission('CREATE_CITERNE', authorities);
  const canUpdate = isAdmin || checkPermission('UPDATE_CITERNE', authorities);
  const canDelete = isAdmin || checkPermission('DELETE_CITERNE', authorities);
  const canAffect = isAdmin || checkPermission('AFFECT_CITERNE', authorities);
  const canCreateJauge = isAdmin || checkPermission('CREATE_JAUGES', authorities);
  const canDecommissionCiterne = isAdmin || checkPermission('DECOMMISSION_CITERNE', authorities);

  const getConsommationForCiterne = (c: Citerne) =>
    consommations.find((agg) => agg.citerne?.id === c.id) ?? null;

  const filteredCiternes = filterListByQuery(citernes, filterQuery, ['code']);
  const qAffect = affectFilterQuery.trim().toLowerCase();
  const filteredAffectations = !qAffect
    ? affectations
    : affectations.filter(
        (a) =>
          String(a.citerne?.code ?? '').toLowerCase().includes(qAffect) ||
          String(a.chantier?.code ?? '').toLowerCase().includes(qAffect)
      );

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditing(null);
    dispatch(fetchCiternes(undefined));
    dispatch(fetchCiterneConsommations());
  };

  const handleDeleteConfirm = () => {
    if (toDelete) {
      dispatch(deleteCiterne(toDelete));
      setToDelete(null);
      setConfirmOpen(false);
      dispatch(fetchCiternes(undefined));
      dispatch(fetchCiterneConsommations());
    }
  };

  return (
    <Page title="Citernes">
      <Tabs
        tabs={[
          { label: 'Citernes', icon: <Fuel className="h-4 w-4" /> },
          { label: 'Affectation', icon: <Link2 className="h-4 w-4" /> },
        ]}
        value={tab}
        onChange={setTab}
      />

      <TabPanel value={tab} index={0}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold">Liste des citernes</h2>
              <FilterInput value={filterQuery} onChange={setFilterQuery} placeholder="Rechercher (code)…" />
            </div>
            <AddButton label="Créer une citerne" disabled={!canCreate} onClick={() => { setEditing(null); setModalOpen(true); }} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredCiternes.map((citerne) => (
              <CiterneCard
                key={citerne.id}
                citerne={citerne}
                consommation={getConsommationForCiterne(citerne)}
                onEdit={(c) => { setEditing(c); setModalOpen(true); }}
                onDelete={(c) => { setToDelete(c); setConfirmOpen(true); }}
                onJauge={(c) => { setCiterneForJauge(c); setJaugeModalOpen(true); }}
                canUpdate={canUpdate}
                canDelete={canDelete}
                canCreateJauge={canCreateJauge}
              />
            ))}
          </div>
          {filteredCiternes.length === 0 && (
            <p className="rounded-lg border border-slate-200 bg-slate-50 py-8 text-center text-slate-600">Aucune citerne</p>
          )}
        </div>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold">Affectation des citernes</h2>
              <FilterInput value={affectFilterQuery} onChange={setAffectFilterQuery} placeholder="Rechercher…" id="affect-filter" />
            </div>
            <AddButton label="Nouvelle affectation" disabled={!canAffect} onClick={() => { setEditingAffectation(null); setAffectModalOpen(true); }} />
          </div>
          <DataTable<Record<string, unknown>>
            columns={[
              { key: 'id', label: 'Id' },
              { key: 'citerne.code', label: 'Citerne' },
              { key: 'chantier.code', label: 'Chantier' },
              { key: 'date_d', label: 'Date début', render: (value) => value ? new Date(value as string).toLocaleDateString('fr-FR') : '—' },
              { key: 'date_f', label: 'Date fin', render: (value) => value ? new Date(value as string).toLocaleDateString('fr-FR') : '—' },
            ]}
            data={filteredAffectations as Record<string, unknown>[]}
            keyField="id"
            emptyMessage="Aucune affectation"
            actions={(row) => (
              <TableActions
                onEdit={() => { const id = (row as CiterneAffectation).id; setEditingAffectation(affectations.find((a) => a.id === id) ?? null); setAffectModalOpen(true); }}
                onDelete={() => { const id = (row as CiterneAffectation).id; setToDeleteAffectation(affectations.find((a) => a.id === id) ?? null); setAffectConfirmOpen(true); }}
                editDisabled={!canAffect}
                deleteDisabled={!canDecommissionCiterne}
              />
            )}
          />
          {totalPages > 1 && (
            <Pagination page={pageNum} totalPages={totalPages} onPageChange={(p) => dispatch(fetchAffectations(p))} />
          )}
        </div>
      </TabPanel>

      <Modal open={modalOpen} onClose={handleCloseModal} title={editing ? 'Modifier la citerne' : 'Nouvelle citerne'}>
        <CiterneForm citerne={editing ?? undefined} type={editing ? 'update' : 'create'} onClose={handleCloseModal} onSuccess={handleCloseModal} />
      </Modal>

      <Modal
        open={jaugeModalOpen}
        onClose={() => { setJaugeModalOpen(false); setCiterneForJauge(null); }}
        title={citerneForJauge ? `Jauge citerne (${citerneForJauge.code ?? citerneForJauge.id})` : 'Jauge citerne'}
      >
        <JaugerForm
          jauge={citerneForJauge ? ({ citerne: { id: citerneForJauge.id, code: citerneForJauge.code } } as unknown as Jauge) : undefined}
          onClose={() => { setJaugeModalOpen(false); setCiterneForJauge(null); }}
          onSuccess={() => { setJaugeModalOpen(false); setCiterneForJauge(null); dispatch(fetchCiterneConsommations()); dispatch(fetchCiternes(undefined)); }}
        />
      </Modal>

      <Modal open={affectModalOpen} onClose={() => setAffectModalOpen(false)} title={editingAffectation ? 'Modifier l\'affectation' : 'Nouvelle affectation'}>
        <CiterneAffectationForm affectation={editingAffectation ?? undefined} type={editingAffectation ? 'update' : 'create'} onClose={() => setAffectModalOpen(false)} onSuccess={() => { setAffectModalOpen(false); dispatch(fetchAffectations(pageNum)); dispatch(fetchCiternes(0)); }} />
      </Modal>

      <ConfirmModal open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDeleteConfirm} message="Voulez-vous supprimer cette citerne ?" />
      <ConfirmModal open={affectConfirmOpen} onClose={() => setAffectConfirmOpen(false)} onConfirm={() => { if (toDeleteAffectation) { dispatch(deleteAffectation(toDeleteAffectation)); setToDeleteAffectation(null); setAffectConfirmOpen(false); } }} message="Voulez-vous supprimer cette affectation ?" />
    </Page>
  );
}
